<template>
  <AdminShell
    page-title="站内信"
    page-subtitle="查看推广支付成功通知，并进入客户已启用推广。"
    active-menu="notifications"
  >
    <template #actions>
      <el-button v-if="isAdmin" plain icon="el-icon-refresh" :loading="loading" @click="fetchNotifications">
        刷新
      </el-button>
      <el-button v-if="isAdmin" type="primary" icon="el-icon-s-promotion" @click="goToPendingPromotions">
        待处理推广
      </el-button>
    </template>

    <section v-if="isAdmin" class="notifications-page">
      <article class="notification-toolbar">
        <div class="notification-summary">
          <strong>{{ unreadCount }}</strong>
          <span>未读通知</span>
        </div>
        <el-switch
          v-model="filters.unreadOnly"
          active-text="仅看未读"
          @change="fetchNotifications"
        />
      </article>

      <article class="notification-list">
        <el-table
          v-loading="loading"
          :data="notifications"
          row-key="id"
          empty-text="暂无站内信通知"
        >
          <el-table-column label="状态" width="96">
            <template slot-scope="{ row }">
              <span class="read-chip" :class="{ 'is-unread': !row.read_at }">
                {{ row.read_at ? '已读' : '未读' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="通知内容" min-width="280">
            <template slot-scope="{ row }">
              <div class="notification-content">
                <strong>{{ row.title || '推广通知' }}</strong>
                <span>{{ row.content || '-' }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="推广" min-width="220">
            <template slot-scope="{ row }">
              <div class="notification-meta">
                <strong>{{ payloadOf(row).campaign_name || '-' }}</strong>
                <span>商户 ID：{{ payloadOf(row).payer_commercial_id || '-' }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="订单" min-width="180">
            <template slot-scope="{ row }">
              <div class="notification-meta">
                <strong>{{ payloadOf(row).order_no || `#${payloadOf(row).order_id || '-'}` }}</strong>
                <span>{{ moneyText(payloadOf(row)) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="通知时间" width="170">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.created_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="150" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                :disabled="!!row.read_at || markingId === row.id"
                :loading="markingId === row.id"
                @click="markAsRead(row)"
              >
                标记已读
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </article>
    </section>

    <section v-else class="notification-denied">
      <i class="el-icon-lock"></i>
      <h2>当前账号无站内信权限</h2>
      <p>站内信模块仅在 commercial_id = 1 的管理员账号下显示。</p>
    </section>
  </AdminShell>
</template>

<script>
import {
  createNotificationStream,
  getUserInfo,
  listNotifications,
  markNotificationRead,
} from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import { formatDateTime, formatMoney, resolveCommercialId } from '@/utils/promotion-form'

export default {
  name: 'NotificationsPage',
  components: {
    AdminShell,
  },
  data() {
    return {
      commercialId: '',
      loading: false,
      markingId: null,
      notifications: [],
      stream: null,
      filters: {
        unreadOnly: false,
        limit: 50,
        offset: 0,
      },
    }
  },
  computed: {
    isAdmin() {
      return String(this.commercialId) === '1'
    },
    unreadCount() {
      return this.notifications.filter(item => !item.read_at).length
    },
  },
  async mounted() {
    await this.initialize()
  },
  beforeDestroy() {
    this.closeStream()
  },
  methods: {
    async initialize() {
      await this.loadCommercialId()
      if (!this.isAdmin) return
      await this.fetchNotifications()
      this.openStream()
    },
    async loadCommercialId() {
      try {
        const { data } = await getUserInfo()
        this.commercialId = String(resolveCommercialId(data) || '')
      } catch (error) {
        this.commercialId = ''
      }
    },
    async fetchNotifications() {
      if (!this.isAdmin) return
      this.loading = true
      try {
        const res = await listNotifications({
          commercial_id: this.commercialId,
          limit: this.filters.limit,
          offset: this.filters.offset,
          unread: this.filters.unreadOnly ? 1 : undefined,
        })
        this.notifications = Array.isArray(res?.data?.items) ? res.data.items : []
      } catch (error) {
        this.notifications = []
        this.$message.error(error.message || '加载站内信失败')
      } finally {
        this.loading = false
      }
    },
    openStream() {
      this.closeStream()
      if (!window.EventSource || !this.isAdmin) return

      try {
        this.stream = createNotificationStream(this.commercialId)
        this.stream.addEventListener('promotion_notification', event => {
          const message = this.parseStreamMessage(event)
          const notification = message && message.payload ? message.payload : null
          if (notification && notification.id) {
            this.$message.success(notification.title || '收到新的站内信通知')
          }
          this.fetchNotifications()
        })
      } catch (error) {
        this.stream = null
      }
    },
    closeStream() {
      if (this.stream && typeof this.stream.close === 'function') {
        this.stream.close()
      }
      this.stream = null
    },
    parseStreamMessage(event) {
      try {
        return JSON.parse(event.data || '{}')
      } catch (_) {
        return null
      }
    },
    async markAsRead(row) {
      if (!row || row.read_at || !this.isAdmin) return
      this.markingId = row.id
      try {
        const res = await markNotificationRead(row.id, this.commercialId)
        const updated = res && res.data ? res.data : null
        this.notifications = this.notifications.map(item => (
          item.id === row.id ? { ...item, ...(updated || { read_at: Date.now() }) } : item
        ))
      } catch (error) {
        this.$message.error(error.message || '标记已读失败')
      } finally {
        this.markingId = null
      }
    },
    payloadOf(row) {
      return row && row.payload && typeof row.payload === 'object' ? row.payload : {}
    },
    moneyText(payload) {
      if (!payload || payload.settlement_amount === undefined || payload.settlement_amount === null) {
        return '结算金额：-'
      }
      return `结算金额：${formatMoney(payload.settlement_amount, payload.settlement_currency || 'USD')}`
    },
    goToPendingPromotions() {
      this.$router.push({
        path: '/promotion',
        query: {
          view: 'pending-promotions',
        },
      })
    },
    formatDateTime,
  },
}
</script>

<style scoped>
.notifications-page {
  display: grid;
  gap: 18px;
}

.notification-toolbar {
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #dbe4f0;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.94);
}

.notification-summary {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  color: #64748b;
}

.notification-summary strong {
  color: #0f172a;
  font-size: 28px;
}

.notification-list {
  border: 1px solid #dbe4f0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  overflow: hidden;
}

.read-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  padding: 5px 10px;
  border-radius: 999px;
  color: #64748b;
  background: #e2e8f0;
  font-size: 12px;
  font-weight: 600;
}

.read-chip.is-unread {
  color: #166534;
  background: #dcfce7;
}

.notification-content,
.notification-meta {
  display: grid;
  gap: 4px;
}

.notification-content strong,
.notification-meta strong {
  color: #0f172a;
  font-size: 14px;
}

.notification-content span,
.notification-meta span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.notification-denied {
  min-height: 360px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 12px;
  color: #64748b;
  border: 1px solid #dbe4f0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
}

.notification-denied i {
  color: #94a3b8;
  font-size: 40px;
}

.notification-denied h2 {
  margin: 0;
  color: #0f172a;
  font-size: 22px;
}

.notification-denied p {
  margin: 0;
}

@media (max-width: 768px) {
  .notification-toolbar {
    align-items: flex-start;
    flex-direction: column;
    padding: 16px;
  }
}
</style>
